import React, { useState, useRef } from "react";
import { Star, ChevronRight, Pencil, Send, ThumbsUp, MessageSquare, Camera, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { timeAgo } from "@/lib/categories";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { ImageViewer } from "@/components/ImageViewer";
import { toast } from "sonner";
import { EmojiButton, EmojiPreview } from "@/components/EmojiButton";
import { EmojiText } from "@/lib/emoji";

const far = (name) => `Mund të shtosh foto vetëm kur je te ${name}`;
const distM = (a, b, c, d) => {
  const r = Math.PI / 180, x = Math.sin(((c - a) * r) / 2) ** 2 + Math.cos(a * r) * Math.cos(c * r) * Math.sin(((d - b) * r) / 2) ** 2;
  return 12742000 * Math.asin(Math.sqrt(x));
};
const getPos = () => new Promise((res, rej) => {
  if (!navigator.geolocation) { rej(new Error("no-geo")); return; }
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), rej, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
});

const SORTS = {
  new: { label: "Më të rejat", fn: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") },
  old: { label: "Më të vjetrat", fn: (a, b) => (a.created_at || "").localeCompare(b.created_at || "") },
  high: { label: "Më të lartat", fn: (a, b) => b.rating - a.rating },
  low: { label: "Më të ultat", fn: (a, b) => a.rating - b.rating },
};

function Stars({ value, size = "w-4 h-4" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`${size} ${n <= value ? "fill-yellow-400 text-yellow-400" : "fill-slate-200 text-slate-200"}`} />)}
    </div>
  );
}

function RateCard({ l, count, onSent, onShowAll }) {
  const { requireAuth } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [pos, setPos] = useState(null);
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const canPhoto = l.lat != null && l.lng != null;
  const addPhoto = () => requireAuth(async () => {
    setBusy(true);
    try {
      const p = await getPos();
      if (distM(p.lat, p.lng, l.lat, l.lng) > 300) { toast.error(far(l.title)); return; }
      setPos(p); camRef.current?.click();
    } catch { toast.error("Aktivizo vendndodhjen (GPS) për të shtuar foto"); }
    finally { setBusy(false); }
  });
  const onShot = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPhotos((ph) => [...ph, r.data.url].slice(0, 5)); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const send = () => requireAuth(async () => {
    if (!rating) { toast.error("Zgjidh yjet për të vlerësuar"); return; }
    try { await api.post(`/listings/${l.id}/reviews`, { rating, comment, images: photos, ...(photos.length && pos ? pos : {}) }); setRating(0); setComment(""); setPhotos([]); toast.success("Faleminderit!"); onSent(); }
    catch (e) { toast.error(errMsg(e)); }
  });
  return (
    <div data-testid="rate-card" className="rounded-3xl border border-emerald-100 bg-emerald-50/40 p-4">
      <div className="flex items-start gap-2">
        <Star className="w-6 h-6 fill-emerald-500 text-emerald-500 shrink-0 mt-0.5" />
        <h2 data-testid="rate-card-title" className="font-extrabold text-slate-900 text-base leading-snug flex-1 min-w-0 line-clamp-2 break-words">Vlerëso {l.title}</h2>
        <button data-testid="rate-card-summary" onClick={onShowAll} className="flex items-center gap-1 shrink-0 text-sm mt-0.5">
          <span className="font-bold text-slate-900">{l.rating || "—"}</span>
          <span className="text-slate-500">({count})</span>
          <ChevronRight className="w-4 h-4 text-slate-500" />
        </button>
      </div>
      <div className="flex justify-center gap-3 mt-4">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)} className="active:scale-90 transition-transform">
            <Star className={`w-9 h-9 ${n <= rating ? "fill-yellow-400 text-yellow-400" : "text-yellow-400"}`} strokeWidth={1.6} />
          </button>
        ))}
      </div>
      <p className="text-center text-sm text-slate-400 mt-2">Prek yjet për të vlerësuar</p>
      <div className="relative mt-4">
        <Pencil className="w-5 h-5 text-slate-400 absolute left-4 top-3.5" />
        <Textarea data-testid="review-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Shkruaj një koment (opsionale)..." className="rounded-2xl bg-white pl-12 min-h-[52px]" rows={1} />
      </div>
      <div className="flex flex-wrap items-center gap-1 mt-1"><EmojiButton inline testid="review-emoji-btn" onPick={(em) => setComment((t) => t + em)} /><EmojiPreview text={comment} testid="review-emoji-preview" /></div>
      {canPhoto && (
        <div className="mt-3">
          <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onShot} className="hidden" data-testid="review-camera-input" />
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <div key={p} className="relative w-16 h-16">
                <img src={p} alt="" className="w-16 h-16 rounded-xl object-cover" />
                <button type="button" data-testid={`review-photo-remove-${i}`} onClick={() => setPhotos(photos.filter((x) => x !== p))} className="absolute -top-1.5 -right-1.5 bg-black/70 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
              </div>
            ))}
            {photos.length < 5 && (
              <button type="button" data-testid="review-add-photo" onClick={addPhoto} disabled={busy} className="h-16 px-4 rounded-xl border-2 border-dashed border-emerald-300 bg-white text-emerald-600 text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
                <Camera className="w-5 h-5" />{busy ? "Duke kontrolluar..." : "Shto foto"}
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">Foto mund të shtosh vetëm kur je te {l.title}.</p>
        </div>
      )}
      <button data-testid="submit-review" onClick={send} className="w-full mt-3 h-12 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
        <Send className="w-5 h-5" />Dërgo vlerësimin
      </button>
    </div>
  );
}

function ReviewItem({ r, isOwner, onChange }) {
  const { user, requireAuth } = useAuth();
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState(r.reply?.text || "");
  const [view, setView] = useState(null);
  const helpful = r.helpful_by || [];
  const liked = user && helpful.includes(user.id);
  const toggle = () => requireAuth(async () => {
    try { const res = await api.post(`/reviews/${r.id}/helpful`); onChange({ ...r, helpful_by: res.data.helpful_by }); } catch (e) { toast.error(errMsg(e)); }
  });
  const saveReply = async () => {
    try { const res = await api.put(`/reviews/${r.id}/reply`, { text }); onChange({ ...r, reply: res.data.reply }); setReplying(false); toast.success("Përgjigjja u ruajt"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`review-${r.id}`} className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="flex gap-3">
        {r.user_avatar ? <img src={r.user_avatar} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" />
          : <div className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold shrink-0">{r.user_name?.[0]}</div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <span className="font-bold text-slate-900 truncate">{r.user_name}</span>
            <span className="text-xs text-slate-400 shrink-0 mt-0.5">{timeAgo(r.created_at)}</span>
          </div>
          <Stars value={r.rating} />
          {r.comment && <p className="text-sm text-slate-700 mt-2 break-words"><EmojiText body={r.comment} /></p>}
          {r.images?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {r.images.map((p, i) => <button key={p} type="button" data-testid={`review-img-${r.id}-${i}`} onClick={() => setView(p)}><img src={p} alt="" className="w-20 h-20 rounded-xl object-cover" /></button>)}
            </div>
          )}
          {view && <ImageViewer src={view} onClose={() => setView(null)} />}
          <div className="flex items-center justify-between mt-3">
            <button data-testid={`review-helpful-${r.id}`} onClick={toggle} className={`flex items-center gap-1.5 text-sm ${liked ? "text-emerald-600 font-semibold" : "text-slate-500"}`}>
              <ThumbsUp className={`w-4 h-4 ${liked ? "fill-emerald-100" : ""}`} />E dobishme{helpful.length ? ` (${helpful.length})` : ""}
            </button>
            {isOwner && <button data-testid={`review-reply-btn-${r.id}`} onClick={() => setReplying(!replying)} className="flex items-center gap-1.5 text-sm text-slate-500"><MessageSquare className="w-4 h-4" />Përgjigju</button>}
          </div>
          {r.reply?.text && !replying && (
            <div data-testid={`review-reply-${r.id}`} className="mt-3 rounded-2xl bg-emerald-50 px-3 py-2">
              <div className="text-xs font-semibold text-emerald-700">Përgjigjja e pronarit</div>
              <p className="text-sm text-slate-700 break-words"><EmojiText body={r.reply.text} /></p>
            </div>
          )}
          {replying && (
            <div className="mt-3">
              <Textarea data-testid={`review-reply-input-${r.id}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Shkruaj përgjigjen..." className="rounded-2xl" />
              <div className="flex flex-wrap items-center gap-1 mt-1"><EmojiButton inline testid={`review-reply-emoji-${r.id}`} onPick={(em) => setText((t) => t + em)} /><EmojiPreview text={text} /></div>
              <button data-testid={`review-reply-save-${r.id}`} onClick={saveReply} className="mt-2 h-10 px-4 rounded-xl bg-emerald-500 text-white text-sm font-semibold">Ruaj përgjigjen</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ReviewsSection({ l, reviews, setReviews, reload }) {
  const { user } = useAuth();
  const [sort, setSort] = useState("new");
  const [all, setAll] = useState(false);
  const isOwner = !!user && String(user.id) === String(l.owner?.id || l.owner_id);
  const list = [...reviews].sort(SORTS[sort].fn);
  const update = (nr) => setReviews(reviews.map((x) => (x.id === nr.id ? nr : x)));
  return (
    <section id="vleresimet" className="px-4 mt-5 scroll-mt-24" data-testid="reviews-section">
      <RateCard l={l} count={reviews.length} onSent={reload} onShowAll={() => setAll(true)} />
      <div className="flex items-center justify-between mt-6 mb-3 gap-2">
        <h2 className="text-base font-extrabold text-slate-900 whitespace-nowrap">Vlerësimet e përdoruesve</h2>
        <select data-testid="reviews-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 min-w-0 rounded-xl border border-slate-200 bg-white px-2 text-sm text-slate-800">
          {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
      </div>
      <div className="space-y-3">
        {(all ? list : list.slice(0, 5)).map((r) => <ReviewItem key={r.id} r={r} isOwner={isOwner} onChange={update} />)}
        {!reviews.length && <p className="text-sm text-slate-400 py-2" data-testid="reviews-empty">Ende pa vlerësime. Bëhu i pari!</p>}
        {reviews.length > 5 && !all && <button data-testid="reviews-show-all" onClick={() => setAll(true)} className="w-full h-11 rounded-2xl border border-emerald-200 text-sm font-semibold text-emerald-600">Lexo të gjitha ({reviews.length})</button>}
      </div>
    </section>
  );
}

export default ReviewsSection;
